import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Truck, Satellite, Zap, Check, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTEREST_OPTIONS = [
  { id: "cybertruck-accessories", label: "Cybertruck Accessories", desc: "Floor mats, wraps, lighting, and aftermarket parts", icon: Truck, color: "#E31937" },
  { id: "cybertruck-mods", label: "Cybertruck Modifications", desc: "Performance upgrades, suspension, and custom builds", icon: Truck, color: "#FF3B30" },
  { id: "cybertruck-camping", label: "Cybertruck Camping", desc: "Camping gear, overlanding setups, and outdoor kits", icon: Truck, color: "#F59E0B" },
  { id: "starlink-home", label: "Starlink Home Internet", desc: "Residential Starlink setup, tips, and optimization", icon: Satellite, color: "#00BFFF" },
  { id: "starlink-mobile", label: "Starlink Mobile / RV", desc: "Starlink Roam, mobile setups, and travel connectivity", icon: Satellite, color: "#33CCFF" },
  { id: "starlink-business", label: "Starlink Business", desc: "Enterprise applications, maritime, and aviation use", icon: Satellite, color: "#0099CC" },
  { id: "tesla-vehicles", label: "Tesla Vehicles", desc: "Model S, 3, X, Y, Semi referrals and accessories", icon: Zap, color: "#22C55E" },
  { id: "tesla-energy", label: "Tesla Energy", desc: "Solar panels, Powerwall, and energy products", icon: Zap, color: "#16A34A" },
  { id: "tesla-lifestyle", label: "Tesla Lifestyle", desc: "Apparel, merchandise, and community events", icon: Zap, color: "#A855F7" },
];

export default function Interests() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const token = localStorage.getItem("ctcsb_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/users/interests`, { headers, withCredentials: true });
        setSelected(res.data.interests || []);
      } catch (err) {
        console.error("Load interests error:", err);
      }
    };
    load();
  }, []);

  const toggle = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    setSaved(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/users/interests`, { interests: selected }, { headers, withCredentials: true });
      setSaved(true);
    } catch (err) {
      console.error("Save interests error:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="interests-page" className="min-h-screen pt-20 pb-12 px-6 md:px-12" style={{ background: '#0A0A0C' }}>
      <div className="max-w-5xl mx-auto">
        <div className="mb-10">
          <span className="text-xs tracking-[0.2em] uppercase text-[#00BFFF] font-semibold">PERSONALIZE</span>
          <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Select Your Interests
          </h1>
          <p className="text-base text-slate-400 mt-3">Choose what excites you most. We'll match you with the best affiliate programs.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#2A2A30] mb-10">
          {INTEREST_OPTIONS.map((opt, i) => {
            const isSelected = selected.includes(opt.id);
            return (
              <motion.button
                key={opt.id}
                onClick={() => toggle(opt.id)}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                data-testid={`interest-${opt.id}`}
                className={`text-left p-6 border border-[#2A2A30] transition-all duration-300 ${
                  isSelected
                    ? "bg-[#1C1C20] border-[#E31937]"
                    : "bg-[#141417] hover:bg-[#1C1C20]"
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <opt.icon className="w-6 h-6" style={{ color: opt.color }} />
                  <div className={`w-5 h-5 border flex items-center justify-center transition-colors ${
                    isSelected ? "bg-[#E31937] border-[#E31937]" : "border-[#2A2A30]"
                  }`}>
                    {isSelected && <Check className="w-3 h-3 text-white" />}
                  </div>
                </div>
                <h3 className="text-sm font-bold text-white mb-1">{opt.label}</h3>
                <p className="text-xs text-slate-500">{opt.desc}</p>
              </motion.button>
            );
          })}
        </div>

        <div className="flex items-center justify-between gap-4">
          <div className="text-sm text-slate-500">
            {selected.length} interest{selected.length !== 1 ? "s" : ""} selected
          </div>
          <div className="flex gap-3">
            <button
              onClick={save}
              disabled={saving || selected.length === 0}
              data-testid="save-interests-button"
              className="px-8 py-3 font-bold uppercase tracking-wide bg-[#E31937] text-white hover:bg-[#FF3B30] transition-colors disabled:opacity-50 text-sm"
            >
              {saving ? "Saving..." : saved ? "Saved!" : "Save Interests"}
            </button>
            {saved && (
              <button
                onClick={() => navigate("/dashboard")}
                data-testid="go-to-dashboard-button"
                className="flex items-center gap-2 px-6 py-3 border border-[#2A2A30] text-white font-bold uppercase tracking-wide hover:border-white hover:bg-white/5 transition-colors text-sm"
              >
                Dashboard <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
